import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@musa.tech/design-system";
import { Badge } from "@/components/ui/badge";
import { Copy, Check, X, Share2, MessageSquare } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PoliticaContent } from "@/hooks/usePoliticasContent";
import { openCloudHumansChat } from "@/utils/cloudHumans";

interface FAQReadModalProps {
  politica: PoliticaContent | null;
  open: boolean;
  onClose: () => void;
  shareUrl: string;
}

const FAQ_BADGE_CLASSES = "bg-muted/60 text-muted-foreground border-0 text-[10px] px-2 py-0.5 font-medium uppercase tracking-wide";

export const FAQReadModal = ({ politica, open, onClose, shareUrl }: FAQReadModalProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copiado para a área de transferência");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Erro ao copiar link");
    }
  };

  if (!politica) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute right-3 top-3 h-8 w-8 rounded-full z-10 bg-background/80 backdrop-blur-sm hover:bg-muted"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Fechar</span>
        </Button>

        {/* Header */}
        <header className="flex-shrink-0 px-8 pt-8 pb-6 border-b border-border/40">
          <div className="space-y-3 pr-8">
            {politica.type && (
              <Badge className={FAQ_BADGE_CLASSES}>
                {politica.type}
              </Badge>
            )}
            <h2 className="text-2xl font-semibold leading-tight text-foreground tracking-tight">
              {politica.title}
            </h2>
          </div>
        </header>
        
        {/* Content */}
        <article className="flex-1 overflow-y-auto px-8 py-6">
          <div 
            className="faq-content"
            dangerouslySetInnerHTML={{ __html: politica.content.html }}
          />
        </article>

        {/* Footer */}
        <footer className="flex-shrink-0 flex justify-between items-center px-8 py-4 border-t border-border/40 bg-muted/30">
          <Button
            size="sm"
            onClick={openCloudHumansChat}
            className="gap-2"
          >
            <MessageSquare className="h-4 w-4" />
            Abrir chamado
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopyLink}
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 text-green-500" />
                <span className="text-green-600">Copiado!</span>
              </>
            ) : (
              <>
                <Share2 className="h-4 w-4" />
                Compartilhar
              </>
            )}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
};
